import React, { useEffect, useState, useRef } from 'react';
import './App.css';
import GameContainer from './components/GameContainer/GameContainer';
import generateGrid from './components/utils/gridGenerator';
import Title from './components/UI/Title';
import { AudioProvider } from './components/utils/AudioContext';
import { GameProvider, useGame } from './components/utils/GameContext'; 
import { AuthProvider, useAuth } from './components/utils/AuthContext';
import BackgroundMusic from './components/audio/BackgroundMusic';
import AccountMenu, { AVATARS } from './components/UI/AccountMenu'; 
import AccountButton from './components/UI/AccountButton';
import LeaderboardMenu from './components/UI/LeaderboardMenu'; 
import LeaderboardButton from './components/UI/LeaderboardButton'; 
import NavBar from './components/UI/NavBar'; 
import Settings from './components/UI/Settings'; 
import FriendsMenu from './components/UI/FriendsMenu';
import AchievementsMenu from './components/UI/AchievementsMenu';
import ChallengeNotification from './components/UI/ChallengeNotification'; 
import Shop from './components/Shop/Shop';
import Tutorial from './components/UI/Tutorial';
import { BUNDLES, getSkin } from './components/Shop/ShopConfig'; 
import { User, UserPlus, CheckCircle, X, Coins, Zap, Trophy } from 'lucide-react';
import { playCollectSound, playBlingSound, playUiClickSound, playUiHoverSound } from './components/utils/sounds';
import { RainingCoins, ConfettiBurst } from './components/UI/Effects'; 
import { soundManager } from './components/utils/SoundManager';

// --- NEW COMPONENTS ---
const SplashScreen = ({ onStart }) => {
    return (
        <div className="splash-screen" onClick={onStart}>
            <div className="splash-content">
                <Title />
                <p className="press-prompt">PRESS TO START</p>
            </div>
        </div>
    );
};

const LoadingScreen = ({ isExiting }) => {
    return (
        <div className={`loading-screen ${isExiting ? 'fade-out' : ''}`}>
            <div className="loading-content">
                <Title />
                <div className="loader-container">
                    <div className="loader-bar"></div>
                </div>
                <p className="loading-text">LOADING...</p>
            </div>
        </div>
    );
};

function InnerApp() {
    const { gameState } = useGame(); 
    
    // Navigation State
    const [activeTab, setActiveTab] = useState('home');

    // Menu States
    const [showAccount, setShowAccount] = useState(false);
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showFriends, setShowFriends] = useState(false);
    const [showAchievements, setShowAchievements] = useState(false); // NEW: Achievements State
    
    // Payment & Claim States
    const [paymentStatus, setPaymentStatus] = useState(null); 
    const [isVerifying, setIsVerifying] = useState(false); 
    const [claimQueue, setClaimQueue] = useState([]); 
    const [showClaimModal, setShowClaimModal] = useState(false);
    
    // Animation States for Modals
    const [isSuccessClosing, setIsSuccessClosing] = useState(false); 
    const [isClaimClosing, setIsClaimClosing] = useState(false);     
    const [isCancelClosing, setIsCancelClosing] = useState(false);   

    // Effect State (Array for multiple simultaneous effects)
    const [activeEffects, setActiveEffects] = useState([]);

    // Invite Logic State
    const [inviterProfile, setInviterProfile] = useState(null);

    const [currentNotification, setCurrentNotification] = useState(null);

    // Score Saved Toast State
    const [showSaveToast, setShowSaveToast] = useState(false);

    const { acceptInviteLink, currentUser, getPublicProfile, userData, dismissNotification, verifyPayment } = useAuth();

    // -- EFFECTS MANAGEMENT --
    const addEffect = (effectData) => {
        const id = Date.now() + Math.random();
        setActiveEffects(prev => [...prev, { ...effectData, id }]);
        
        setTimeout(() => {
            setActiveEffects(prev => prev.filter(e => e.id !== id));
        }, 4000);
    };

    useEffect(() => {
        if (userData?.challengeNotifications) {
            const keys = Object.keys(userData.challengeNotifications);
            if (keys.length > 0) {
                const id = keys[0];
                setCurrentNotification({ id, ...userData.challengeNotifications[id] });
            } else {
                setCurrentNotification(null);
            }
        }
    }, [userData]);

    // CHECK FOR PAYMENT STATUS FROM URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get('status');
        const sessionId = params.get('session_id');

        if (status === 'success' && sessionId) {
            setPaymentStatus('success');
            setIsVerifying(true); 
            
            window.history.replaceState({}, document.title, "/");
            
            verifyPayment(sessionId).then(result => {
                setIsVerifying(false); 
                if (result && result.success) {
                    playCollectSound(false);
                    prepareClaimQueue(result.bundleId);
                } else {
                    alert("Verification failed. Please contact support.");
                }
            });

        } else if (status === 'cancel') {
            setPaymentStatus('cancel');
            window.history.replaceState({}, document.title, "/");
        }
    }, [verifyPayment]);

    const prepareClaimQueue = (bundleId) => {
        const bundle = BUNDLES.find(b => b.id === bundleId);
        if (!bundle) return;

        const queue = [];
        if (bundle.contents.coins) {
            queue.push({ id: 'coins', type: 'currency', amount: bundle.contents.coins, claimed: false });
        }
        if (bundle.contents.skins) {
            bundle.contents.skins.forEach(skinId => {
                queue.push({ id: skinId, type: 'skin', data: getSkin(skinId), claimed: false });
            });
        }
        if (bundle.contents.noAds) {
            queue.push({ id: 'no_ads', type: 'feature', name: "NO ADS", claimed: false });
        }
        setClaimQueue(queue);
    };

    const handleContinueFromSuccess = () => {
        if (isVerifying) return; 
        setIsSuccessClosing(true);
    };

    const handleSuccessAnimationEnd = (e) => {
        if (isSuccessClosing && e.target.classList.contains('payment-modal-content')) {
            setPaymentStatus(null);
            if (claimQueue.length > 0) {
                setShowClaimModal(true);
            }
            setIsSuccessClosing(false);
        }
    };

    const handleClaimItem = (index, e) => {
        playBlingSound(false);
        const newQueue = [...claimQueue];
        newQueue[index].claimed = true;
        setClaimQueue(newQueue);

        const x = e.clientX;
        const y = e.clientY;

        const item = claimQueue[index];
        if (item.type === 'currency') {
            addEffect({ type: 'rain' });
        } else {
            addEffect({ type: 'confetti', x, y });
        }
    };

    const handleCloseClaim = () => {
        setIsClaimClosing(true);
    };

    const handleClaimAnimationEnd = (e) => {
        if (isClaimClosing && e.target.classList.contains('payment-modal-overlay')) {
            setShowClaimModal(false);
            setClaimQueue([]);
            setIsClaimClosing(false);
        }
    };

    const handleCloseCancel = () => {
        setIsCancelClosing(true);
    };

    const handleCancelAnimationEnd = (e) => {
        if (isCancelClosing && e.target.classList.contains('payment-modal-overlay')) {
            setPaymentStatus(null);
            setIsCancelClosing(false);
        }
    };

    const handleDismissNotification = () => {
        if (currentNotification) {
            dismissNotification(currentNotification.id, currentNotification);
            setCurrentNotification(null);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const inviteUid = params.get('invite');
        
        if (inviteUid && currentUser && inviteUid !== currentUser.uid) {
            const fetchInviter = async () => {
                const profile = await getPublicProfile(inviteUid);
                if (profile) {
                    setInviterProfile(profile);
                    window.history.replaceState({}, document.title, "/");
                }
            };
            fetchInviter();
        }
    }, [currentUser, getPublicProfile]);

    const handleAcceptInvite = async () => {
        if (inviterProfile) {
            await acceptInviteLink(inviterProfile.uid);
            setInviterProfile(null);
        }
    };

    const getAvatarConfig = (id) => {
        const avatar = AVATARS.find(a => a.id === id);
        return avatar ? avatar : { icon: <User />, color: '#fff' };
    };

    const handleHomeClick = () => {
        setActiveTab('home');
        setShowSettings(false);
        setShowAccount(false);
        setShowLeaderboard(false);
        setShowFriends(false);
        setShowAchievements(false); // Close achievements when navigating
    };

    const handleSettingsClick = () => {
        setActiveTab('settings');
        setShowSettings(true);
        setShowAccount(false);
        setShowLeaderboard(false);
        setShowFriends(false);
        setShowAchievements(false); // Close achievements when navigating
    };

    const handleFriendsClick = () => {
        setActiveTab('friends');
        setShowFriends(true);
        setShowSettings(false);
        setShowAccount(false);
        setShowLeaderboard(false);
        setShowAchievements(false); // Close achievements when navigating
    };

    const handleShopClick = () => {
        setActiveTab('shop');
        setShowSettings(false);
        setShowFriends(false);
        setShowAccount(false);
        setShowLeaderboard(false);
        setShowAchievements(false); // Close achievements when navigating
    };

    const handleLoginRedirect = () => {
        setActiveTab('home'); 
        setShowLeaderboard(false);
        setShowFriends(false);
        setShowAchievements(false); // Close achievements when navigating
        setShowAccount(true); 
    };

    const handleScoreSaved = () => {
        setShowSaveToast(true);
        setTimeout(() => setShowSaveToast(false), 3000);
    };

    // UPDATE: Added !showAchievements to menu check
    const isMainMenu = 
        activeTab === 'home' && 
        !showAccount && 
        !showLeaderboard && 
        !showSettings && 
        !showFriends && 
        !showAchievements && 
        !inviterProfile &&
        gameState === 'ready';
        
    const allClaimed = claimQueue.length > 0 && claimQueue.every(i => i.claimed);
    
    const showSuccess = paymentStatus === 'success';
    const showClaim = showClaimModal;
    const isAnyRewardModalOpen = showSuccess || showClaim;

    return (
        <div className={`body`}>
            
            {/* Global Effects Container */}
            {activeEffects.map(effect => (
                <React.Fragment key={effect.id}>
                    {effect.type === 'rain' && <RainingCoins count={effect.count || 150} />}
                    {effect.type === 'confetti' && <ConfettiBurst x={effect.x} y={effect.y} />}
                </React.Fragment>
            ))}

            {/* Score Saved Toast */}
            <div style={{
                position: 'fixed',
                top: '20px',
                left: '50%',
                transform: showSaveToast ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(-100px)',
                backgroundColor: '#22c55e',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                zIndex: 2000,
                opacity: showSaveToast ? 1 : 0,
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                fontWeight: 'bold',
                pointerEvents: 'none'
            }}>
                <CheckCircle size={24} />
                <span>Score Saved Successfully!</span>
            </div>

            <AccountButton onClick={() => setShowAccount(true)} />
            <LeaderboardButton onClick={() => setShowLeaderboard(true)} />

            {/* NEW: On-screen Achievements Button */}
            {isMainMenu && (
                <button 
                    onClick={() => {
                        playUiClickSound(false);
                        setShowAchievements(true);
                    }}
                    onMouseEnter={() => playUiHoverSound(false)}
                    style={{
                        position: 'fixed',
                        top: '80px', // Below the leaderboard button
                        right: '20px',
                        width: '45px',
                        height: '45px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(250, 204, 21, 0.2), rgba(249, 115, 22, 0.2))',
                        border: '2px solid rgba(250, 204, 21, 0.5)',
                        color: '#facc15',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 100,
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.5), 0 0 10px rgba(250, 204, 21, 0.3)',
                        transition: 'transform 0.2s, background 0.2s',
                    }}
                    onMouseOver={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(250, 204, 21, 0.4), rgba(249, 115, 22, 0.4))';
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(250, 204, 21, 0.2), rgba(249, 115, 22, 0.2))';
                    }}
                >
                    <Trophy size={22} />
                </button>
            )}

            <Title />
            
            {/* UPDATE: Added showAchievements to isMenuOpen */}
            <GameContainer 
                startingGrid={generateGrid(2, [0, 0], 0)} 
                isMenuOpen={showAccount || showLeaderboard || showSettings || showFriends || showAchievements || activeTab !== 'home' || !!inviterProfile || (!!currentNotification && isMainMenu) || isAnyRewardModalOpen}
                onOpenLogin={() => setShowAccount(true)}
                onScoreSaved={handleScoreSaved}
            />
            
            <NavBar 
                activeTab={activeTab}
                onHome={handleHomeClick}
                onShop={handleShopClick}
                onFriends={handleFriendsClick}
                onSettings={handleSettingsClick}
            />

            {showAccount && <AccountMenu onClose={() => setShowAccount(false)} />}
            
            {showLeaderboard && (
                <LeaderboardMenu 
                    onClose={() => setShowLeaderboard(false)} 
                    onOpenLogin={handleLoginRedirect}
                />
            )}

            {showSettings && <Settings onClose={() => {
                setShowSettings(false);
                setActiveTab('home');
            }} />}

            {showFriends && (
                <FriendsMenu 
                    onClose={() => {
                        setShowFriends(false);
                        setActiveTab('home');
                    }} 
                    onOpenLogin={handleLoginRedirect}
                />
            )}

            {/* NEW: Render Achievements Menu */}
            {showAchievements && (
                <AchievementsMenu onClose={() => setShowAchievements(false)} />
            )}

            {activeTab === 'shop' && (
                <Shop onClose={handleHomeClick} onOpenLogin={handleLoginRedirect} onAddEffect={addEffect} />
            )}
            
            {currentNotification && isMainMenu && (
                <ChallengeNotification 
                    notification={currentNotification} 
                    currentMoney={userData?.money || 0}
                    onDismiss={handleDismissNotification}
                />
            )}

            {isAnyRewardModalOpen && (
                <div 
                    className={`payment-modal-overlay ${isClaimClosing ? 'closing' : ''}`}
                    onAnimationEnd={handleClaimAnimationEnd}
                >
                    {/* SUCCESS CONTENT */}
                    {showSuccess && !showClaim && (
                        <div 
                            className={`payment-modal-content success ${isSuccessClosing ? 'closing-content' : 'opening-content'}`}
                            onAnimationEnd={handleSuccessAnimationEnd}
                        >
                            <div className="payment-sunburst"></div>
                            <div className="payment-icon-wrapper success">
                                <CheckCircle size={48} />
                            </div>
                            <h2>PAYMENT SUCCESSFUL!</h2>
                            <p>Thank you for your support.</p>
                            
                            <div className="payment-note">
                                {isVerifying ? "(Verifying transaction...)" : "(Items verified)"}
                            </div>

                            <button 
                                className="payment-btn success" 
                                onClick={handleContinueFromSuccess}
                                disabled={isVerifying}
                                style={{ 
                                    opacity: isVerifying ? 0.6 : 1, 
                                    cursor: isVerifying ? 'wait' : 'pointer' 
                                }}
                            >
                                {isVerifying ? "VERIFYING..." : "CONTINUE"}
                            </button>
                        </div>
                    )}

                    {/* CLAIM ITEMS CONTENT */}
                    {showClaim && (
                        <div className="payment-modal-content claim-items opening-content">
                            <h2 style={{color: 'white', fontSize:'1.4rem'}}>ITEMS RECEIVED</h2>
                            <div className="claim-list">
                                {claimQueue.map((item, idx) => (
                                    <div key={idx} className={`claim-row ${item.claimed ? 'claimed' : ''}`}>
                                        <div className="claim-icon">
                                            {item.type === 'currency' && <Coins size={24} color="#fbbf24" />}
                                            {item.type === 'feature' && <Zap size={24} color="#a855f7" />}
                                            {item.type === 'skin' && <div className={`skin-preview ${item.data.cssClass}`}></div>}
                                        </div>
                                        <div className="claim-info">
                                            <div className="claim-name">
                                                {item.type === 'currency' ? `${item.amount} Coins` : (item.name || item.data.name)}
                                            </div>
                                            <div className="claim-type">{item.type}</div>
                                        </div>
                                        <button 
                                            className="btn-claim-item" 
                                            disabled={item.claimed}
                                            onClick={(e) => handleClaimItem(idx, e)}
                                        >
                                            {item.claimed ? "OWNED" : "CLAIM"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button 
                                className={`payment-btn ${allClaimed ? 'success' : 'cancel'}`} 
                                onClick={allClaimed ? handleCloseClaim : null}
                                disabled={!allClaimed}
                                style={{opacity: allClaimed ? 1 : 0.5}}
                            >
                                {allClaimed ? "CLOSE" : "CLAIM ALL TO CONTINUE"}
                            </button>
                        </div>
                    )}
                </div>
            )}
            
            {paymentStatus === 'cancel' && (
                <div 
                    className={`payment-modal-overlay ${isCancelClosing ? 'closing' : ''}`}
                    onClick={handleCloseCancel}
                    onAnimationEnd={handleCancelAnimationEnd}
                >
                    <div className="payment-modal-content cancel" onClick={(e) => e.stopPropagation()}>
                        <div className="payment-icon-wrapper cancel">
                            <X size={48} />
                        </div>
                        <h3>Payment Cancelled</h3>
                        <p>No worries! You were not charged.</p>
                        <button className="payment-btn cancel" onClick={handleCloseCancel}>
                            CLOSE
                        </button>
                    </div>
                </div>
            )}
            
            {inviterProfile && (
                <div className="invite-modal-overlay">
                    <div className="invite-modal-content">
                        <div className="invite-header-icon">
                            <UserPlus size={32} />
                        </div>
                        <h3>Friend Invite</h3>
                        
                        <div className="inviter-preview">
                            <div 
                                className="inviter-avatar"
                                style={{ 
                                    borderColor: getAvatarConfig(inviterProfile.avatarId).color,
                                    color: getAvatarConfig(inviterProfile.avatarId).color 
                                }}
                            >
                                {getAvatarConfig(inviterProfile.avatarId).icon}
                            </div>
                            <span className="inviter-name">{inviterProfile.username}</span>
                        </div>

                        <p>Accept friend invite from <br/> <span className="highlight-username">{inviterProfile.username}</span>?</p>
                        
                        <div className="invite-actions">
                            <button className="modal-btn cancel" onClick={() => setInviterProfile(null)}>
                                NO
                            </button>
                            <button className="modal-btn confirm" onClick={handleAcceptInvite}>
                                YES, ACCEPT
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

// Wrapper to handle Splash Screen State
function AppContent() {
    const [appState, setAppState] = useState('splash'); // 'splash', 'loading', 'tutorial', 'ready'
    const [exitLoading, setExitLoading] = useState(false);

    const needsTutorial = () => {
        return localStorage.getItem('tileSprint_tutorialComplete') !== 'true';
    };

    const handleStart = async () => {
        setAppState('loading');
        
        try {
            // Initialize and resume audio context on user gesture
            soundManager.init();
            await soundManager.resumeContext();
        } catch (e) {
            console.error("Audio init failed", e);
        }

        // Wait for bar to fill (1.8s duration) before starting exit sequence
        setTimeout(() => {
            setExitLoading(true);
            // Wait for exit animation (0.5s)
            setTimeout(() => {
                if (needsTutorial()) {
                    setAppState('tutorial');
                } else {
                    setAppState('ready');
                }
            }, 500); 
        }, 1800);
    };

    const handleTutorialComplete = () => {
        setAppState('ready');
    };

    if (appState === 'splash') {
        return <SplashScreen onStart={handleStart} />;
    }

    if (appState === 'loading') {
        return <LoadingScreen isExiting={exitLoading} />;
    }

    if (appState === 'tutorial') {
        return <Tutorial onComplete={handleTutorialComplete} />;
    }

    return (
        <>
            <BackgroundMusic />
            <InnerApp />
        </>
    );
}

function App() {
    return (
        <AuthProvider>
            <AudioProvider>
                <GameProvider>
                    <AppContent />
                </GameProvider>
            </AudioProvider>
        </AuthProvider>
    )
}

export default App;